import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { Loader2 } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { getMemberActivityReport, getTrainingFrequencyReport } from '@/lib/statistics-api'
import { shareOf, TRAINING_FREQUENCY_LABELS } from '@/lib/statistics'

const percentFormat = new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 0 })
const decimalFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

const ACTIVE_WINDOWS = [
    { days: 14, key: 'active_14' },
    { days: 21, key: 'active_21' },
    { days: 30, key: 'active_30' },
] as const

function LoadingState() {
    return <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
}

export function MemberActivityReport({ from, to }: { from: string; to: string }) {
    const activityQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'member-activity', to],
        queryFn: () => getMemberActivityReport({ to }),
    })

    const frequencyQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'training-frequency', from, to],
        queryFn: () => getTrainingFrequencyReport({ from, to }),
    })

    const activity = activityQuery.data
    const frequency = frequencyQuery.data ?? []
    const frequencyTotal = frequency.reduce((sum, row) => sum + row.member_count, 0)
    const avgPerWeek = frequency[0]?.avg_per_week ?? null

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Aktive Mitglieder</CardTitle>
                    <CardDescription>
                        Mitglieder mit mindestens einem Training im jeweiligen Zeitraum vor dem Stichtag
                        {activity ? ` (${format(parseISO(activity.reference_at), 'dd.MM.yyyy')})` : ''}.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {activityQuery.isLoading ? <LoadingState /> : activityQuery.isError ? (
                        <p className="text-sm text-destructive">{getErrorMessage(activityQuery.error)}</p>
                    ) : activity ? (
                        <div className="grid gap-3 sm:grid-cols-3">
                            {ACTIVE_WINDOWS.map(({ days, key }) => (
                                <div key={key} className="rounded-md bg-muted/50 p-4">
                                    <p className="text-sm text-muted-foreground">Letzte {days} Tage</p>
                                    <p className="text-2xl font-semibold tabular-nums">{activity[key]}</p>
                                    <p className="text-sm text-muted-foreground tabular-nums">
                                        {percentFormat.format(shareOf(activity[key], activity.members_total))} von {activity.members_total} Mitgliedern
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Trainingsfrequenz</CardTitle>
                    <CardDescription>
                        Trainings pro Woche je Mitglied im gewählten Zeitraum. Wer im Zeitraum beigetreten ist, wird ab dem Beitritt gemessen.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {frequencyQuery.isLoading ? <LoadingState /> : frequencyQuery.isError ? (
                        <p className="text-sm text-destructive">{getErrorMessage(frequencyQuery.error)}</p>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Ø{' '}
                                <span className="text-lg font-semibold text-foreground tabular-nums">
                                    {avgPerWeek === null ? '–' : decimalFormat.format(avgPerWeek)}
                                </span>{' '}
                                Trainings pro Woche (Mitglieder mit mindestens einem Training)
                            </p>
                            <div className="space-y-2">
                                {frequency.map((row) => {
                                    const share = shareOf(row.member_count, frequencyTotal)
                                    return (
                                        <div key={row.bucket} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_5.5rem] items-center gap-3 text-sm">
                                            <span className="truncate">{TRAINING_FREQUENCY_LABELS[row.bucket]}</span>
                                            <div className="h-3 rounded-full bg-muted">
                                                <div className="h-full rounded-full bg-primary" style={{ width: `${share * 100}%` }} />
                                            </div>
                                            <span className="text-right tabular-nums text-muted-foreground">
                                                {row.member_count} · {percentFormat.format(share)}
                                            </span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
